import { InfiniteTable, InputDecimal } from '@/components/_common';
import { quantityFormatter } from '@/utils/number';
import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { ColumnsType } from 'antd/lib/table/interface';
import { getTableScrollX } from '@/utils/table';
import { useMaterialReturnList } from '@/core/domain/stock/MaterialReturn/MaterialReturn.hooks';
import {
  MaterialReturnListRequest,
  ReturnMaterialFormValues,
  ReturnMaterialResponseList,
  ReturnMaterialType,
} from '@/core/domain/stock/MaterialReturn/MaterialReturn.types';
import useConfigurationColumnsPart from '@/hooks/useConfigurationColumnsPart';
import { Form } from 'antd';
import { WorkorderConfigurationCompany } from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.types';
import { flatInfiniteResult } from '@/utils/request/flatInfiniteResult';

interface Props {
  config?: WorkorderConfigurationCompany;
  params?: MaterialReturnListRequest;
  setRowsSelected: React.Dispatch<React.SetStateAction<string[]>>;
  rowsSelected: string[];
}

const ReturnMaterialList: React.FC<Props> = ({
  config,
  params,
  setRowsSelected,
  rowsSelected,
}) => {
  const intl = useIntl();

  const form = Form.useFormInstance<ReturnMaterialFormValues>();

  const { data, isLoading, fetchNextPage, hasNextPage, dataUpdatedAt } =
    useMaterialReturnList(params);

  const returnMaterialListData = flatInfiniteResult(data);

  useEffect(() => {
    if (returnMaterialListData.length) {
      form.setFieldValue(
        'items',
        returnMaterialListData.map((item) => ({
          ...item,
          returnedQuantity: item.quantityDelivered,
        })),
      );
    }
  }, [dataUpdatedAt]);

  const columns: ColumnsType<ReturnMaterialResponseList> = [
    {
      key: 'workOrderNumber',
      dataIndex: 'workOrderNumber',
      title: intl.formatMessage({
        id: 'deliveryOrReturnOfMaterials.returnMaterial.table.column.workOrder',
      }),
      ellipsis: true,
      width: 100,
    },
    {
      key: 'description',
      dataIndex: 'description',
      title: intl.formatMessage({
        id: 'deliveryOrReturnOfMaterials.returnMaterial.table.column.description',
      }),
      width: 300,
      ellipsis: true,
    },
    useConfigurationColumnsPart({ barcode: 'barCode', internalCode: 'sku' }),
    {
      key: 'licensePlate',
      dataIndex: 'licensePlate',
      title: intl.formatMessage({
        id: 'deliveryOrReturnOfMaterials.returnMaterial.table.column.licensePlate',
      }),
      ellipsis: true,
      width: 100,
      render: (value) => (value ? value : '-'),
    },
    {
      key: 'costPrice',
      dataIndex: 'costPrice',
      title: 'Preço de Custo',
      ellipsis: true,
      width: 100,
      render: (value) => (value ? quantityFormatter(value) : '-'),
    },
    {
      key: 'quantityDelivered',
      dataIndex: 'quantityDelivered',
      title: intl.formatMessage({
        id: 'deliveryOrReturnOfMaterials.returnMaterial.table.column.qtyDelivered',
      }),
      ellipsis: true,
      width: 120,
      render: (value: number) => quantityFormatter(value),
    },
    {
      key: 'returnedQuantity',
      dataIndex: 'returnedQuantity',
      title: intl.formatMessage({
        id: 'deliveryOrReturnOfMaterials.returnMaterial.table.column.qtyGiveBack',
      }),
      ellipsis: true,
      width: 170,
      render(_, row, index) {
        return (
          <Form.Item
            getValueFromEvent={(value) => {
              if (value) {
                setRowsSelected((prev) => {
                  return [...prev, row.differentiator];
                });
              } else {
                setRowsSelected((prev) => {
                  return [...prev];
                });
              }
              return value;
            }}
            name={[index, 'returnedQuantity']}
            initialValue={row.quantityDelivered}
            noStyle
          >
            <InputDecimal max={row.quantityDelivered} />
          </Form.Item>
        );
      },
    },
  ];

  return (
    <Form.List name="items">
      {() => (
        <InfiniteTable<ReturnMaterialResponseList>
          rowSelection={{
            preserveSelectedRowKeys: true,
            onChange: (keys) => setRowsSelected(keys as string[]),
            selectedRowKeys: rowsSelected,
          }}
          size="middle"
          loading={isLoading}
          rowKey={(row) => row.differentiator}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          columns={columns}
          dataSource={
            config?.isAutomaticStock
              ? returnMaterialListData.filter(
                  (item) => item.type == ReturnMaterialType.consumable,
                )
              : returnMaterialListData
          }
          scroll={{
            x: getTableScrollX(columns),
          }}
        />
      )}
    </Form.List>
  );
};

export default ReturnMaterialList;
